import {AcaHeader} from "./AcaHeader";
import {
    IonButton,
    IonCheckbox, IonCol,
    IonContent, IonGrid, IonIcon,
    IonInput,
    IonItem,
    IonLoading,
    IonPage, IonRow, IonSelect, IonSelectOption, IonText,
    IonTextarea, IonToggle, useIonAlert, useIonModal
} from "@ionic/react";
import React, {useEffect} from "react";
import {AcaGetPipelineStagesResponseStage, ContactTypeBrief} from "./types";
import {
    ACA_BATCH_UPDATE,
    ACA_GET_PIPELINE_STAGES,
    ACA_SEARCH,
    ACA_UPDATE_CONTACTS,
    ACA_UPDATE_OPPORTUNITIES,
    ACA_GET_COMPLIANCE_REASONS
} from "../../schemas/aca";
import {useMutation} from "@apollo/client";
import {formatDate, formatDatetime, formatStatus} from "../../utils";
import useAuth from "../../hooks/useAuth";
import {useHistory} from "react-router";
import ModalMoreDetails from "./modals/ModalMoreDetails";
import {
    documentOutline,
    documentSharp,
    personOutline,
    personSharp,
    refreshOutline,
    refreshSharp,
    trashOutline, trashSharp
} from "ionicons/icons";
import AcaUploadXLSXFile, {AcaSingleUploadI} from "./components/AcaUploadXLSXFile";

let searchDelayTimeout: NodeJS.Timeout | null = null;
let uploadDelayTimeout: NodeJS.Timeout | null = null;
const AcaBatchUpdate: React.FC<any> = () => {

    const {claims} = useAuth();
    const history = useHistory();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [presentAlert, dismissAlert] = useIonAlert();
    const [searchText, setSearchText] = React.useState<string>("");
    const [searchResults, setSearchResults] = React.useState<any>(undefined);
    const [selectedRecords, setSelectedRecords] = React.useState<any[] | undefined>(undefined);
    const [batchUploadFile, setBatchUploadFile] = React.useState<string | undefined>(undefined);
    const [batchUploadFilename, setBatchUploadFilename] = React.useState<string | undefined>(undefined);
    const [batchUpdateType, setBatchUpdateType] = React.useState<'SEARCH' | 'UPLOAD'>('SEARCH');
    const [lookupType, setLookupType] = React.useState<'SEARCH' | 'CONTACT_IDS'>('CONTACT_IDS');
    const [updateType, setUpdateType] = React.useState<'CONTACT_STATUS' | 'OPPORTUNITY_STAGE' | 'COMPLIANCE_FLAG'>('CONTACT_STATUS');
    const [contactIdStr, setContactIdStr] = React.useState<string | null | undefined>('');
    const [openOpportunitiesFound, setOpenOpportunitiesFound] = React.useState<boolean>(false);
    const [mismatchOpportunityStagesFound, setMismatchOpportunityStagesFound] = React.useState<boolean>(false);
    const [allEPVOppsFound, setAllEPVOppsFound] = React.useState<boolean>(false);
    const [selectedCustomerUpdateStatus, setSelectedCustomerUpdateStatus] = React.useState<'NO_CONTACT' | 'DECEASED' | 'PREVIOUS' | 'NOT_ELIGIBLE' | undefined>(undefined);

    const [availableStages, setAvailableStages] = React.useState<AcaGetPipelineStagesResponseStage[] | undefined>(undefined);
    const [newStage, setNewStage] = React.useState<number | undefined>(undefined);
    const [tier1, setTier1] = React.useState<boolean>(false);
    const [tier2, setTier2] = React.useState<boolean>(false);
    const [manager, setManager] = React.useState<boolean>(false);

    const [reasons, setReasons] = React.useState<{ id:number; description:string; status?:string }[] | undefined>(undefined);
    const [complianceFlag, setComplianceFlag] = React.useState<boolean | undefined>(undefined);
    const [complianceReasonId, setComplianceReasonId] = React.useState<number | undefined>(undefined);
    const [complianceReasonText, setComplianceReasonText] = React.useState<string | undefined>(undefined);

    const complianceSubmitError = React.useMemo(() => {
        if (updateType !== 'COMPLIANCE_FLAG') {
            return null;
        }

        // required if comp flag is true
        if (complianceFlag === true) {
            if (!complianceReasonId) {
                return 'Select a compliance reason.';
            }
            // required if reason id 3 selected
            if (complianceReasonId === 3 && !complianceReasonText?.trim()) {
                return 'You selected "Other" - please enter a reason note.';
            }
        }
        return null;
    }, [updateType, complianceFlag, complianceReasonId, complianceReasonText]);


    const [acaSearchMutation] = useMutation(ACA_SEARCH);
    const [acaUpdateContacts] = useMutation(ACA_UPDATE_CONTACTS);
    const [acaUpdateOpportunities] = useMutation(ACA_UPDATE_OPPORTUNITIES);
    const [getStagesMutation] = useMutation(ACA_GET_PIPELINE_STAGES);
    const [acaBatchUpdateMutation] = useMutation(ACA_BATCH_UPDATE);
    const [getComplianceReasons] = useMutation(ACA_GET_COMPLIANCE_REASONS);
    const [presentMoreDetails, dismissMoreDetails] = useIonModal(ModalMoreDetails, {
        dismiss: (data: string, role: string) => dismissMoreDetails(data, role),
        pipelineStage: availableStages?.find((stage) => stage.id===newStage)
    });

    const [orderColumn, setOrderColumn] = React.useState<string>('CREATED_DATETIME');
    const [orderDirection, setOrderDirection] = React.useState<string>('DESC');

    const updateSearchKeyword = async (searchStr: string) => {
        if (searchDelayTimeout) {
            clearTimeout(searchDelayTimeout);
        }
        searchDelayTimeout = setTimeout(() => {
            searchByKeyword(searchStr);
        }, 2000);
    };

    const searchByKeyword = async (searchStr: string | number | null | undefined) => {
        if (!searchStr || (typeof searchStr === "number")) {
            return;
        }
        setIsLoading(true);
        if (searchDelayTimeout) {
            clearTimeout(searchDelayTimeout);
        }
        acaSearchMutation({variables: {data: {quickSearch: searchStr, pageSize: 100, currentPage: 1, orderColumn: orderColumn, orderDirection: orderDirection}}}).then(results => {
            if (!results.data.acaSearch?.contacts) {
                setSearchResults(undefined)
            } else {
                setSearchResults(results);
            }
        }).catch(error => {
            presentAlert(error.message);
        }).finally(() => {
            setIsLoading(false);
        });
        setSearchText(searchStr);
    };

    const pullStages = async (status: 'OPEN' | 'CLOSED_WON' | 'CLOSED_LOST') => {
        const stagesResponse = await getStagesMutation({variables: {pipelineId: 1}});
        if (!stagesResponse.data.acaGetPipelineStages.success) {
            presentAlert(stagesResponse.data.acaGetPipelineStages.error)
        } else {
            if (openOpportunitiesFound) {
                setAvailableStages(stagesResponse.data.acaGetPipelineStages.stages.filter((stage: AcaGetPipelineStagesResponseStage) => !stage.enrolled && !stage.failed));
            } else {
                setAvailableStages(stagesResponse.data.acaGetPipelineStages.stages.filter((stage: AcaGetPipelineStagesResponseStage) => !stage.failed && !stage.closedOption));
            }
        }
    };

    const checkboxClick = async (add: boolean, id: number) => {
        if (add) {
            let records = selectedRecords;
            if (!records) {
                records = [];
            }
            const recToAdd = searchResults.data.acaSearch.contacts.find((contact:ContactTypeBrief) => contact.contactId === id);
            if (records.length === 0) {
                setIsLoading(true);
                await pullStages(recToAdd.lastOpportunity.status);
                setIsLoading(false);
            }
            records.push(recToAdd);
            setSelectedRecords(undefined)
            setTimeout(() => setSelectedRecords(records), 100);
        } else {
            let records = selectedRecords;
            if (!records) {
                records = [];
            }
            let previousStagesMatched: boolean = true;
            let initialStage = records[0].lastOpportunity.stageId;
            records.forEach(contact => {
                if (initialStage !== contact.lastOpportunity?.stageId) {
                    previousStagesMatched = false;
                }
            });

            records = records.filter((contact:ContactTypeBrief) => contact.contactId !== id);
            if (updateType === 'OPPORTUNITY_STAGE' && !previousStagesMatched) {
                let newStagesMatched: boolean = true;
                let initalStage2 = records[0].lastOpportunity.stageId;
                records.forEach(contact => {
                    if (initalStage2 !== contact.lastOpportunity?.stageId) {
                        newStagesMatched = false;
                    }
                });
                if (records.length > 0 && newStagesMatched) {
                    setIsLoading(true);
                    await pullStages(records[0].lastOpportunity.status);
                    setIsLoading(false);
                }
            }
            setSelectedRecords(undefined)
            setTimeout(() => setSelectedRecords(records), 100);
        }
    };


    const searchByContactIds = async () => {
        if (contactIdStr && contactIdStr.length > 0) {
            setIsLoading(true);
            const contactIds = contactIdStr.split("\n").map((id) => Number(id));

            acaSearchMutation({
                variables: {
                    data: {
                        contactIds: contactIds,
                        pageSize: 100,
                        currentPage: 1,
                        orderColumn: orderColumn,
                        orderDirection: orderDirection
                    }
                }
            }).then(results => {
                if (!results.data.acaSearch?.contacts) {
                    setSelectedRecords(undefined);
                    setIsLoading(false);
                } else {
                    setSelectedRecords(results.data.acaSearch.contacts);
                    if (results.data.acaSearch.contacts.length > 0) {
                        pullStages(results.data.acaSearch.contacts[0].lastOpportunity.status)
                            .finally(() => {
                                setIsLoading(false);
                            });
                    } else {
                        setIsLoading(false);
                    }

                }
            }).catch(error => {
                presentAlert(error.message);
                setIsLoading(false);
            });
        }
    };

    const submitContactUpdate = async () => {

        if (!selectedCustomerUpdateStatus) {
            presentAlert('Please select a new customer status first');
            return;
        }

        setIsLoading(true);
        let contacts: {contactId: number, status: 'NOT_ELIGIBLE' | 'PREVIOUS' | 'DECEASED' | 'NO_CONTACT'}[] = [];
        selectedRecords?.forEach((c) => {
            contacts.push({contactId: c.contactId, status: selectedCustomerUpdateStatus});
        });
        await acaUpdateContacts({variables: {data: {contacts}}});

        if (lookupType === 'CONTACT_IDS') {
            searchByContactIds();
        } else {
            acaSearchMutation({variables: {data: {quickSearch: searchText, pageSize: 100, currentPage: 1, orderColumn: orderColumn, orderDirection: orderDirection}}}).then(results => {
                if (!results.data.acaSearch?.contacts) {
                    setSearchResults(undefined)
                } else {
                    setSearchResults(results);
                    // update selected record data
                    if (selectedRecords && selectedRecords.length > 0) {
                        let tmpSelectedRecords = selectedRecords;
                        let count = 0;
                        for (let record of tmpSelectedRecords) {
                            const foundInResults = results.data.acaSearch.contacts.find((searchRecord:any) => searchRecord.contactId === record.contactId);
                            if (foundInResults) {
                                tmpSelectedRecords[count] = foundInResults;
                            }
                            count++;
                        }
                        setSelectedRecords(tmpSelectedRecords);
                    }
                }
                presentAlert("Records updated successfully.");
            }).catch(error => {
                presentAlert(error.message);
            }).finally(() => {
                setIsLoading(false);
            });
        }
    };

    const submitOppUpdate = async () => {
        if (!selectedRecords || selectedRecords.length === 0 || !availableStages || availableStages.length === 0) {
            return;
        }

        setIsLoading(true);


        let updateOppResponse = await acaUpdateOpportunities({
            variables: {
                data: {
                    opportunities: selectedRecords.map((rec) => ({opportunityId: rec.lastOpportunity.opportunityId, stageId: newStage}))
                }
            }
        });
        if (!updateOppResponse.data.acaUpdateOpportunities.success) {
            setIsLoading(false);
            return presentAlert(updateOppResponse.data.acaUpdateOpportunities.error);
        }

        if (lookupType === 'CONTACT_IDS') {
            await searchByContactIds();
        } else {
            acaSearchMutation({variables: {data: {quickSearch: searchText, pageSize: 100, currentPage: 1, orderColumn: orderColumn, orderDirection: orderDirection}}})
                .then(results => {
                    if (!results.data.acaSearch?.contacts) {
                        setSearchResults(undefined)
                    } else {
                        setSearchResults(results);
                        // update selected record data
                        refreshSelectedRecords();
                    }
                }).catch(error => {
                    presentAlert(error.message);
                }).finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const submitComplianceUpdate = async () => {
        if (!selectedRecords || selectedRecords.length === 0) {
            return;
        }

        // validation
        const flag = complianceFlag;
        const reasonId = typeof complianceReasonId === 'number' ? complianceReasonId : undefined;
        const reasonText = (complianceReasonText || '').trim();

        if (typeof flag !== 'boolean') {
            return presentAlert('Please choose Yes/No for Flag for Compliance.');
        }
        if (flag) {
            // must have reasonId
            if (!reasonId && !reasonText) {
                return presentAlert('When flagging, provide a reason ID.');
            }
            // if Other require text
            const other = reasons?.find(r => r.description?.toLowerCase() === 'other') || reasons?.find(r => r.id === 3);
            if ((reasonId && other && reasonId === other.id) && !reasonText) {
                return presentAlert('Reason text is required when "Other" is selected.');
            }
        }

        setIsLoading(true);
        try {
            const payload = {
                opportunities: selectedRecords.map((rec) => ({
                    opportunityId: rec?.lastOpportunity?.opportunityId,
                    complianceFlag: flag,
                    complianceReasonId: reasonId ?? null,
                    complianceReasonText: reasonText || null,
                }))
            };

            const res = await acaUpdateOpportunities({ variables: { data: payload } });
            if (!res?.data?.acaUpdateOpportunities?.success) {
                return presentAlert(res?.data?.acaUpdateOpportunities?.error || 'Update failed.');
            }

            // refresh selection view
            if (lookupType === 'CONTACT_IDS') {
                await searchByContactIds();
            } else {
                await refreshSelectedRecords();
            }
            presentAlert('Compliance updates submitted successfully.');
        } catch (e:any) {
            presentAlert(e.message || 'Unknown error');
        } finally {
            setIsLoading(false);
        }
    };

    const refreshSelectedRecords = async () => {
        if (selectedRecords && selectedRecords.length > 0) {

            // get select list results
            const contactIds = selectedRecords.map((record) => record.contactId);
            acaSearchMutation({
                variables: {
                    data: {
                        contactIds: contactIds,
                        pageSize: 100,
                        currentPage: 1,
                        orderColumn: orderColumn,
                        orderDirection: orderDirection
                    }
                }
            }).then(results => {
                if (!results.data.acaSearch?.contacts) {
                    setSelectedRecords(undefined);
                    setIsLoading(false);
                } else {
                    setSelectedRecords(results.data.acaSearch.contacts);
                    if (results.data.acaSearch.contacts.length > 0) {
                        pullStages(results.data.acaSearch.contacts[0].lastOpportunity.status)
                            .finally(() => {
                                setIsLoading(false);
                            });
                    } else {
                        setIsLoading(false);
                    }

                }
            }).catch(error => {
                presentAlert(error.message);
                setIsLoading(false);
            });

        }
    };

    const updateStage = async (stageId: number | undefined | null) => {
        if (!stageId || isNaN(stageId)) {
            return;
        }
        if (newStage !== stageId && availableStages) {
            setNewStage(stageId);
        }
    };


    const addUploadFile = async ({ filename, url, error }: AcaSingleUploadI) => {
        if (error) {
            return alert(error);
        }
        setBatchUploadFile(url);
        setBatchUploadFilename(filename);
        if (uploadDelayTimeout) {
            clearTimeout(uploadDelayTimeout);
        }
        uploadDelayTimeout = setTimeout(() => {
            setBatchUploadFile(undefined);
            setBatchUploadFilename(undefined);
        }, 300000);
    };


    const processFileUpload = async (fileUrl: string | undefined) => {
        if (fileUrl) {
            setIsLoading(true);
            const processFileResult = await acaBatchUpdateMutation({variables: {fileUrl: fileUrl}});
            setIsLoading(false);
            if (!processFileResult || !processFileResult.data.acaBatchUpdate) {
                presentAlert('Unknown error');
            }
            if (!processFileResult.data.acaBatchUpdate.success) {
                presentAlert(processFileResult.data.acaBatchUpdate.error);
            } else {
                presentAlert('Records processed successfully.');
                setBatchUploadFile(undefined);
            }
        }
    };

    useEffect(() => {
        if (selectedRecords && selectedRecords.length > 0) {
            setOpenOpportunitiesFound(false);
            setMismatchOpportunityStagesFound(false);
            setAllEPVOppsFound(true);
            let currentStage = selectedRecords[0].lastOpportunity.stageId;
            for (let record of selectedRecords) {
                if (record.lastOpportunity.stageId !== 3) {
                    setAllEPVOppsFound(false);
                }
                if (record.lastOpportunity.status === 'OPEN') {
                    setOpenOpportunitiesFound(true);
                }
                if (currentStage !== record.lastOpportunity.stageId) {
                    setMismatchOpportunityStagesFound(true);
                }
            }
        }
    }, [selectedRecords]);

    useEffect(() => {
        if (selectedRecords && selectedRecords.length>0) {
            if (updateType === 'COMPLIANCE_FLAG') {
                return; //skip pulling stages
            }
            setIsLoading(true);
            pullStages(selectedRecords[0].status)
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [updateType]);

    useEffect(() => {
        // load this list of users

        // check for roles
        if (!claims.rules.includes('AcaView')) {
            history.push('/');
            return;
        }
        if (claims.rules.includes('AcaEditTier2')) {
            setTier2(true);
        }
        if (claims.rules.includes('AcaEditTier1')) {
            setTier1(true);
        }
        if (claims.rules.includes('AcaManager')) {
            setManager(true);
        } else {
            // no manager rule found, so redirect
            history.push('/');
            return;
        }

    }, []);

    useEffect(() => {
        (async () => {
            if (updateType === 'COMPLIANCE_FLAG' && !reasons) {
                try {
                    const res = await getComplianceReasons();
                    const items = res.data?.acaGetComplianceReasons?.reasons || [];
                    setReasons(items.filter((r:any) => r.status === 'ACTIVE' || r.status === undefined));
                } catch (e:any) {
                    console.error(e);
                    return {success: false, error: e.message};
                }
            }
        })();
    }, [updateType, reasons, getComplianceReasons]);

    useEffect(() => {
        if (complianceReasonId !== 3) {
            setComplianceReasonText('');
        }
    }, [complianceReasonId]);


    const updateBatchUpdateType = async (checked: boolean) => {
        setBatchUpdateType(!checked ? 'SEARCH' : 'UPLOAD');
    }

    const updateLookupType = async(checked: boolean) => {
        setLookupType(checked ? 'SEARCH' : 'CONTACT_IDS');
    }

    return (
        <IonPage>

            <AcaHeader pageTitle="ACA Opportunity Details"></AcaHeader>
            <IonLoading isOpen={isLoading} />
            <IonContent fullscreen className="aca-detail">
                <div style={{padding: '10px'}}>
                    <div className="panel panel-default">
                        <h3>Customer Batch Update</h3>
                        <p>You may search for the records you would like to update or upload a XLSX file:</p>
                        <p style={{backgroundColor: '#eeeeee', padding: '5px'}}><b>Batch Update Type:</b>
                            &nbsp;
                            {batchUpdateType === 'SEARCH' && <span style={{textShadow: '4px 4px 4px rgba(0,0,0,0.4)', fontWeight: 'bold'}}>Search</span>}
                            {batchUpdateType !== 'SEARCH' && <>Search</>}
                            <IonToggle id="batchUpdateType" key="batchUpdateType" style={{verticalAlign: 'middle'}} onIonChange={(e) => updateBatchUpdateType(e.detail.checked)}></IonToggle>
                            &nbsp;
                            {batchUpdateType === 'UPLOAD' && <span style={{textShadow: '4px 4px 4px rgba(0,0,0,0.4)', fontWeight: 'bold'}}>Upload XLSX</span>}
                            {batchUpdateType !== 'UPLOAD' && <>Upload XLSX</>}

                        </p>


                        {batchUpdateType === 'SEARCH' &&
                            <>
                                <p>Choose if you want to provide a list of contact ids or search for contacts/opportunities:</p>
                                <p style={{backgroundColor: '#eeeeee', padding: '5px'}}><b>Search Type:</b>
                                    &nbsp;
                                    {lookupType === 'CONTACT_IDS' && <span style={{textShadow: '4px 4px 4px rgba(0,0,0,0.4)', fontWeight: 'bold'}}>Paste List Of Contact IDs</span>}
                                    {lookupType !== 'CONTACT_IDS' && <>Paste List Of Contact IDs</>}

                                    <IonToggle id="searchType" key="searchType" style={{verticalAlign: 'middle'}} onIonChange={(e) => updateLookupType(e.detail.checked)}></IonToggle>
                                    &nbsp;
                                    {lookupType === 'SEARCH' && <span style={{textShadow: '4px 4px 4px rgba(0,0,0,0.4)', fontWeight: 'bold'}}>Search For Contacts</span>}
                                    {lookupType !== 'SEARCH' && <>Search For Contacts</>}
                                </p>


                                <p>Choose what you want to update:</p>
                                <p style={{backgroundColor: '#eeeeee', padding: '5px', display: 'flex', alignItems: 'center', gap: '6px'}}><b style={{whiteSpace: 'nowrap'}}>Update Type:</b>
                                    &nbsp;
                                    <IonSelect interface="popover" value={updateType} onIonChange={(e) => setUpdateType(e.detail.value)}
                                        style={{ display: 'inline-block', minWidth: '260px', verticalAlign: 'middle' }}>
                                        <IonSelectOption value="CONTACT_STATUS">Contact Status</IonSelectOption>
                                        <IonSelectOption value="OPPORTUNITY_STAGE">Opportunity Stages</IonSelectOption>
                                        <IonSelectOption value="COMPLIANCE_FLAG">Compliance Flag/Reason</IonSelectOption>
                                    </IonSelect>
                                </p>
                                {lookupType === 'SEARCH' &&
                                    <>
                                        <h4>Search</h4>
                                        <p>Search by contact id, first name, last name, phone, or email address.</p>
                                        <IonInput fill="solid" label="Search Keyword" labelPlacement="floating" onIonChange={(e) => updateSearchKeyword(e.detail.value ? e.detail.value : '')} onKeyUp={(e) => e.keyCode === 13 ? searchByKeyword(e.currentTarget.value) : ''}></IonInput>
                                    </>
                                }
                                {lookupType === 'CONTACT_IDS' &&
                                    <div style={{display: 'inline-block'}}>
                                        <h4>Contact ID Lookup</h4>
                                        <p>Enter a list of contact ids seperated by a new line.</p>
                                        <IonTextarea fill="solid" style={{maxWidth: '400px', width: '100%'}} rows={8} cols={10} onIonChange={(e) => setContactIdStr(e.detail.value)}></IonTextarea>

                                        <p style={{textAlign: 'right'}}>
                                            <IonButton onClick={() => searchByContactIds()}>Lookup</IonButton>
                                        </p>
                                    </div>
                                }
                            </>
                        }


                        {batchUpdateType === 'UPLOAD' &&
                            <div>
                                <p><b>Batch File Upload:</b></p>
                                {!batchUploadFile && <IonItem key="upload"><AcaUploadXLSXFile callback={addUploadFile}/></IonItem>}
                                {batchUploadFile &&
                                    <div style={{display: 'inline-block'}}>
                                        <p>
                                            {batchUploadFilename}
                                            &nbsp;
                                            <IonButton fill="clear" size="small" onClick={() => setBatchUploadFile(undefined)}><IonIcon md={trashOutline} ios={trashSharp}></IonIcon></IonButton>
                                            <br />
                                            <span style={{color: '#cccccc', fontStyle: 'italic'}}>* File names are modified for uniqueness after upload.</span>
                                        </p>
                                        <p style={{textAlign: 'right'}}><IonButton onClick={() => processFileUpload(batchUploadFile)}>Submit Batch File Update</IonButton></p>

                                    </div>
                                }
                                <p><a href="https://paygo-ams-public.s3.us-west-2.amazonaws.com/connectCare/ACA_Batch_Update_Template_clean.xlsx" style={{marginTop: '10px'}} target="_blank">Download Template</a></p>
                            </div>
                        }
                    </div>

                    {batchUpdateType === 'SEARCH' && lookupType === 'SEARCH' &&
                        <>
                            <p><b>Search Text:</b> {searchText}</p>
                            <div className="panel">
                                <h4>Step 1: Search</h4>

                                <h6>Search Results</h6>
                                <p><b>Number of
                                    records:</b> {searchResults?.data?.acaSearch?.contacts ? searchResults.data.acaSearch.contacts.length : 0}
                                </p>
                                {(!searchText) &&
                                    <p><i>Please search first.</i></p>
                                }
                                <p className="ion-color-secondary"><i>* Note: Search is limited to the first 100 results.</i></p>
                                {searchResults?.data?.acaSearch?.contacts && searchResults.data.acaSearch.contacts.length > 0 &&
                                    <div style={{maxHeight: '600px', overflow: 'auto', paddingRight: '20px'}}>
                                        <table className="table aca-healthcare" style={{
                                            borderSpacing: '0',
                                            borderCollapse: 'collapse',
                                            width: '100%'
                                        }} key={"head1"}>
                                            <thead key={'search_head1'}>
                                            <tr>
                                                <th></th>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Phone</th>
                                                <th>Email</th>
                                                <th>Address</th>
                                                <th>Contact Status</th>
                                                <th>Number of Opps</th>
                                                <th>Last Opp Status</th>
                                                <th>Last Opp Stage</th>
                                                <th>Last Opp Date</th>
                                            </tr>
                                            </thead>
                                            <tbody key={'search_body1'}>
                                            {searchResults && searchResults.data?.acaSearch?.contacts && searchResults.data.acaSearch.contacts.length > 0 &&
                                                searchResults.data.acaSearch.contacts.map((contact: ContactTypeBrief) => {
                                                    return (
                                                        <tr key={"search_result_" + contact.contactId}>
                                                            <td>
                                                                <IonCheckbox id={"search_chk_" + contact.contactId} checked={selectedRecords && selectedRecords.find((rec) => rec.contactId === contact.contactId)} value={contact.contactId} onClick={(e) => checkboxClick(e.currentTarget.checked, e.currentTarget.value)}></IonCheckbox>
                                                            </td>
                                                            <td style={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>{contact.contactId}</td>
                                                            <td style={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>
                                                                {contact.firstName} {contact.lastName}
                                                                &nbsp;
                                                                <a href={'/aca/view-contact/' + contact.contactId} target={"_blank"} title="Open in a new tab">
                                                                    <IonIcon md={personOutline} ios={personSharp}></IonIcon>
                                                                </a>
                                                            </td>
                                                            <td style={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>{contact.phone}</td>
                                                            <td style={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>{contact.email}</td>
                                                            <td style={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>
                                                            <address>{contact.addressLine1}<br/>
                                                                    {contact.addressLine2 && <>{contact.addressLine2}<br/></>}
                                                                    {contact.addressCity}, {contact.addressState} {contact.addressZip}
                                                                </address>
                                                            </td>
                                                            <td style={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>{formatStatus(contact.status)}</td>
                                                            <td style={{textAlign: 'center'}}>{contact.opportunityCount}</td>
                                                            <td style={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>
                                                                {formatStatus(contact.lastOpportunity?.status)}
                                                                {contact.lastOpportunity &&
                                                                    <>
                                                                        &nbsp;
                                                                        <a href={'/aca/view-opportunity/' + contact.lastOpportunity?.opportunityId} target={"_blank"} title="Open in a new tab">
                                                                            <IonIcon md={documentOutline} ios={documentSharp}></IonIcon>
                                                                        </a>
                                                                    </>
                                                                }
                                                            </td>
                                                            <td style={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>{contact.lastOpportunity?.stageName}</td>
                                                            <td style={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>{formatDate(contact.lastOpportunity?.lastUpdatedDatetime)}</td>
                                                        </tr>
                                                    );
                                                })
                                            }
                                            </tbody>
                                        </table>
                                    </div>
                                }
                            </div>
                        </>
                    }


                    {batchUpdateType === 'SEARCH' && ((searchResults && searchResults.data && searchResults.data.acaSearch && searchResults.data.acaSearch.contacts.length > 0)
                            || (selectedRecords && selectedRecords.length > 0)) &&
                        <div className="panel">
                            <h4>Step 2: Select Records to Update</h4>
                            <div style={{float: 'right', verticalAlign: 'top'}}>
                                <IonButton fill="outline" onClick={() => refreshSelectedRecords()}><IonIcon md={refreshOutline} ios={refreshSharp}></IonIcon></IonButton>
                            </div>
                            <h6>Records to Update</h6>
                            <p><b>Number of records to update:</b> {selectedRecords ? selectedRecords.length : 0}</p>
                            {(!selectedRecords || selectedRecords.length === 0) &&
                                <p><i>Please search and select records first.</i></p>
                            }
                            {selectedRecords && selectedRecords.length > 0 &&
                                <table className="table aca-healthcare" style={{
                                    borderSpacing: '0',
                                    borderCollapse: 'collapse',
                                    width: '100%'
                                }} id={"table2"}>
                                    <thead key={"table_head2"}>
                                    <tr>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Phone</th>
                                        <th>Email</th>
                                        <th>Address</th>
                                        <th>Contact Status</th>
                                        <th>Number of Opps</th>
                                        <th>Last Opp Status</th>
                                        <th>Last Opp Stage</th>
                                        <th>Last Opp Date</th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {selectedRecords && selectedRecords.length > 0 &&
                                        selectedRecords.map((contact: ContactTypeBrief) => {
                                            if (contact.contactId) {
                                                return (
                                                    <tr key={"selected_result_" + contact.contactId}>
                                                        <td>{contact.contactId}</td>
                                                        <td>
                                                            {contact.firstName} {contact.lastName}
                                                            &nbsp;
                                                            <a href={'/aca/view-contact/' + contact.contactId} target={"_blank"} title="Open in a new tab">
                                                                <IonIcon md={personOutline} ios={personSharp}></IonIcon>
                                                            </a>
                                                        </td>
                                                        <td>{contact.phone}</td>
                                                        <td>{contact.email}</td>
                                                        <td>
                                                        <address>{contact.addressLine1}<br/>
                                                                {contact.addressLine2 && <>{contact.addressLine2}<br/></>}
                                                                {contact.addressCity}, {contact.addressState} {contact.addressZip}
                                                            </address>
                                                        </td>
                                                        <td>{formatStatus(contact.status)}</td>
                                                        <td style={{textAlign: 'center'}}>{contact.opportunityCount}</td>
                                                        <td>
                                                            {formatStatus(contact.lastOpportunity?.status)}
                                                            {contact.lastOpportunity &&
                                                                <>
                                                                    &nbsp;
                                                                    <a href={'/aca/view-opportunity/' + contact.lastOpportunity?.opportunityId} target={"_blank"} title="Open in a new tab">
                                                                        <IonIcon md={documentOutline} ios={documentSharp}></IonIcon>
                                                                    </a>
                                                                </>
                                                            }
                                                        </td>
                                                        <td>{contact.lastOpportunity?.stageName}</td>
                                                        <td>{formatDatetime(contact.lastOpportunity?.lastUpdatedDatetime)}</td>
                                                        <td>
                                                            <IonButton size="small" onClick={() => checkboxClick(false, Number(contact.contactId))}>Remove</IonButton>
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                        })
                                    }
                                    </tbody>
                                </table>
                            }
                        </div>
                    }

                    {batchUpdateType === 'SEARCH' && selectedRecords && selectedRecords.length > 0 &&
                        <div className="panel">
                            <h4>Step 3: Update {updateType === 'CONTACT_STATUS' ? 'Contact Status' : updateType === 'OPPORTUNITY_STAGE' ? 'Opportunity Stage' : 'Compliance Flag'}</h4>
                            {openOpportunitiesFound && updateType === 'CONTACT_STATUS' &&
                                <div className="message danger">
                                    Open opportunities found!!!<br />
                                    When batch updating customer statuses, you may only batch update contacts without
                                    any open opportunities.
                                </div>
                            }
                            {mismatchOpportunityStagesFound && updateType === 'OPPORTUNITY_STAGE' &&
                                <div className="message danger">
                                    Opportunities must be in the same stage!!!<br />
                                    When updating opportunities in batch, they must be all in the same stage to ensure
                                    the right action is applied to each opportunity correctly.
                                </div>
                            }
                            {updateType === 'CONTACT_STATUS' &&
                                <IonGrid>
                                    <IonRow>
                                        <IonCol>
                                            <IonSelect fill="outline" label="Status" labelPlacement="floating" disabled={openOpportunitiesFound} onIonChange={(e) => setSelectedCustomerUpdateStatus(e.detail.value)}>

                                                <IonSelectOption value="NO_CONTACT">No Contact</IonSelectOption>
                                                <IonSelectOption value="DECEASED">Deceased</IonSelectOption>
                                                <IonSelectOption value="PREVIOUS">Previous</IonSelectOption>

                                                <IonSelectOption value="NOT_ELIGIBLE">Not Eligible</IonSelectOption>
                                            </IonSelect>
                                        </IonCol>
                                        <IonCol>
                                            <IonButton disabled={openOpportunitiesFound} onClick={() => submitContactUpdate()}>Submit Update</IonButton>
                                        </IonCol>
                                    </IonRow>
                                </IonGrid>
                            }
                            {updateType === 'OPPORTUNITY_STAGE' && availableStages &&
                                <>
                                    <IonGrid>
                                        <IonRow>
                                            <IonCol>
                                                <IonSelect fill="outline" label="Change Stage" labelPlacement="floating" value={newStage} onIonChange={(e) => updateStage(e.detail.value)} disabled={mismatchOpportunityStagesFound}>
                                                    {availableStages.map((item, index) => {
                                                        if (openOpportunitiesFound) {
                                                            if (item.failed) { // failed
                                                            } else if (item.enrolled) { // enrolled stage, hide
                                                            } else if (item.id === 1) { // block new
                                                            } else if (item.id === 3) { // enrolled pending verification
                                                            } else {
                                                                return (
                                                                    <IonSelectOption value={item.id} key={'selectOptStage3_' + item.id}>{item.name}</IonSelectOption>
                                                                )
                                                            }
                                                        } else {
                                                            if (allEPVOppsFound && item.enrolled) { // all selected opps are enrolled pending verification
                                                                return (
                                                                    <IonSelectOption value={item.id} key={'selectOptStage3_' + item.id}>{item.name}</IonSelectOption>
                                                                )
                                                            }
                                                        }
                                                    })
                                                    }
                                                </IonSelect>
                                            </IonCol>
                                            <IonCol>
                                                <IonButton disabled={mismatchOpportunityStagesFound} onClick={() => submitOppUpdate()}>Submit Update</IonButton>
                                            </IonCol>
                                        </IonRow>
                                    </IonGrid>

                                </>
                            }
                            {updateType === 'COMPLIANCE_FLAG' && (
                                <IonGrid>
                                    <IonRow>
                                        <IonCol size="12" sizeMd="4">
                                            <IonSelect interface="popover" fill="outline" label="Flag for Compliance" labelPlacement="floating"
                                                value={typeof complianceFlag === 'boolean' ? (complianceFlag ? 'TRUE' : 'FALSE') : undefined}
                                                onIonChange={(e) => setComplianceFlag(e.detail.value === 'TRUE')}
                                            >
                                                <IonSelectOption value="TRUE">Yes</IonSelectOption>
                                                <IonSelectOption value="FALSE">No</IonSelectOption>
                                            </IonSelect>
                                        </IonCol>

                                        <IonCol size="12" sizeMd="6">
                                            <IonSelect interface="popover" fill="outline" label="Compliance Reason" labelPlacement="floating"
                                                value={complianceReasonId ?? undefined}
                                                onIonChange={(e) => setComplianceReasonId(e.detail.value)}
                                                disabled={complianceFlag !== true}
                                            >
                                                {reasons?.map(r => (
                                                    <IonSelectOption key={r.id} value={r.id}>{r.description}</IonSelectOption>
                                                ))}
                                            </IonSelect>
                                        </IonCol>

                                        <IonCol size="12" sizeMd="6">
                                            <IonTextarea fill="outline" label="Reason Note" labelPlacement="floating"
                                                value={complianceReasonText ?? ''}
                                                onIonChange={(e) => setComplianceReasonText(e.detail.value || '')}
                                                //only enable note if reasonid= 3
                                                disabled={!(complianceFlag === true && complianceReasonId === 3)}
                                            />
                                        </IonCol>

                                        <IonCol size="12" sizeMd="4">
                                            <IonButton
                                                onClick={() => submitComplianceUpdate()}
                                                disabled={!!complianceSubmitError}
                                            >
                                                Submit Update
                                            </IonButton>
                                            {complianceSubmitError && (
                                                <div style={{ marginTop: 8 }}>
                                                    <IonText color="danger">{complianceSubmitError}</IonText>
                                                </div>
                                            )}
                                        </IonCol>
                                    </IonRow>
                                </IonGrid>
                            )}
                        </div>
                    }


                    <div style={{height: '100px'}}>

                    </div>
                </div>
                </IonContent>
                </IonPage>
                );
            }

            export default AcaBatchUpdate;
